<template>
  <div class="dspy-test-page">
    <h1>DSPy Prompt Generation Test</h1>
    <p>Use this page to test the DSPy prompt generation in isolation.</p>
    
    <div class="form-container card">
      <div class="form-group">
        <label for="description">Enter a natural language description for a check:</label>
        <textarea
          id="description"
          v-model="description"
          class="form-control"
          rows="5"
          placeholder="e.g., Check that the policy effective date is before January 1st, 2024"
        ></textarea>
      </div>
      
      <button @click="generateDraft" class="btn btn-primary" :disabled="loading">
        <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        {{ loading ? 'Generating...' : 'Generate Draft' }}
      </button>
    </div>
    
    <div v-if="error" class="error-message">
      <h3>Error</h3>
      <pre>{{ error }}</pre>
    </div>
    
    <div v-if="draftResult" class="result-container card">
      <h3>Generated JSON Draft</h3>
      <pre>{{ formattedResult }}</pre>
      <div class="save-section">
        <button @click="saveDraft" class="btn btn-success" :disabled="isSaving">
          <span v-if="isSaving" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          {{ isSaving ? 'Saving...' : 'Save as New Check' }}
        </button>
        <p v-if="saveSuccess" class="save-success-message">Successfully saved new check!</p>
        <p v-if="saveError" class="error-message">{{ saveError }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import api from '@/services/api';

export default {
  name: 'DSPyTestPage',
  setup() {
    const description = ref('');
    const loading = ref(false);
    const error = ref(null);
    const draftResult = ref(null);
    const isSaving = ref(false);
    const saveError = ref(null);
    const saveSuccess = ref(false);

    const formattedResult = computed(() => {
      return draftResult.value ? JSON.stringify(draftResult.value, null, 2) : '';
    });

    const generateDraft = async () => {
      loading.value = true;
      error.value = null;
      draftResult.value = null;

      try {
        const response = await api.post('/dspy-test-generate/', {
          text_description: description.value,
        });
        
        console.log("Full Axios response object:", response);

        draftResult.value = response.data;
      } catch (e) {
        console.error("DSPy test generation failed:", e);
        error.value = e.response ? e.response.data : e.message;
      } finally {
        loading.value = false;
      }
    };

    const saveDraft = async () => {
      if (!draftResult.value) return;

      isSaving.value = true;
      saveError.value = null;
      saveSuccess.value = false;

      try {
        const draftData = JSON.parse(draftResult.value);

        console.log("Data inside saveDraft (after parsing):", draftData);

        const payload = {
          name: draftData.name,
          description: draftData.description,
          severity: draftData.suggested_severity,
          prompt_template: draftData.prompt_template,
          check_type: 'ANALYSIS',
          is_system: false,
          is_visible_to_users: true
        };

        console.log("Payload being sent to API:", payload);

        await api.post('/automated-checks/', payload);
        saveSuccess.value = true;
      } catch (e) {
        console.error("Failed to save check:", e);
        saveError.value = e.response ? JSON.stringify(e.response.data) : e.message;
      } finally {
        isSaving.value = false;
      }
    };

    return {
      description,
      loading,
      error,
      draftResult,
      formattedResult,
      generateDraft,
      isSaving,
      saveError,
      saveSuccess,
      saveDraft,
    };
  },
};
</script>

<style scoped>
.dspy-test-page {
  padding: 2rem;
}
.form-container, .result-container {
  margin-top: 2rem;
  padding: 2rem;
}
.form-group {
  margin-bottom: 1rem;
}
.error-message {
  margin-top: 2rem;
  padding: 1rem;
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: 0.25rem;
}
pre {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 0.25rem;
  white-space: pre-wrap;
  word-wrap: break-word;
}
.save-section {
  margin-top: 1.5rem;
  border-top: 1px solid #eee;
  padding-top: 1.5rem;
}
.save-success-message {
  color: #155724;
  background-color: #d4edda;
  border-color: #c3e6cb;
  padding: .75rem 1.25rem;
  margin-top: 1rem;
  border: 1px solid transparent;
  border-radius: .25rem;
}
</style> 